import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { tap, Subject, debounceTime } from 'rxjs';
import { FreeCompaniesService } from 'src/app/services/free-companies.service';
import { FreeCompanies, RequestStatus } from 'src/app/shared/data/models';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-list-free-companies',
  templateUrl: './list-free-companies.component.html',
  styleUrls: ['./list-free-companies.component.scss']
})
export class ListFreeCompaniesComponent implements OnInit {
  displayedColumns: string[] = [ 'date', 'name', 'company', 'telephone' ,'allied', 'group', 'facilitator', 'update', 'hide'];
  @Output() public totalItemsList = new EventEmitter<number>();
  private subs = new SubSink();
  private readonly searchSubject = new Subject<string | undefined>();
  public dataSource = new MatTableDataSource<FreeCompanies>([]);
  public length: number = 0;
  public search = '';
  public pageSize: number = 10;
  public pageIndex: number = 1;
  public sortField: string = 'created_At';
  public sortDirection: string = 'desc';
  public statusDetail: RequestStatus = 'init';

  // Lists to store all data
  public alliedList: any[] = [];
  public groupList: any[] = [];
  public facilitatorList: any[] = [];

   // Track changes
  public hasChanges: boolean = false;

  constructor(private freeCompaniesService: FreeCompaniesService,
              private translateService: TranslateService,
              private router: Router,
              private _snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.loadInitilData();
    this.freeCompaniesService.action$.subscribe(() => {
      this.updateAllCompanies(); // Llama al método cuando el servicio emite el evento
    });

    this.subs.sink = this.searchSubject
    .pipe(debounceTime(300))
    .subscribe((searchTerm: string | undefined) => {
      this.search = searchTerm || '';
      this.loadData();
    });

  }

  loadInitilData() {
    // Load allieds, groups, and facilitators
    this.freeCompaniesService.listFreeAllied().subscribe((rta: any) => {
      this.alliedList = rta.allieds;
    });

    this.freeCompaniesService.listFreeGroups().subscribe((rta: any) => {
      this.groupList = rta.groups;
    });

    this.freeCompaniesService.listFreeFacilitator().subscribe((rta: any) => {
      this.facilitatorList = rta.facilitators;
    });


    this.loadData();
    this.freeCompaniesService.Refreshrequired.subscribe(() => {
      this.loadData();
    });
  }

  onCreateNotification() {
    this.markAsChanged();
  }

  public onSearchQueryInput(event: Event): void {
    const searchQuery = (event.target as HTMLInputElement).value;
    this.pageIndex = 0; // Reset to the first page on search
    this.searchSubject.next(searchQuery?.trim());
  }

  sortData(sort: Sort) {
    //this.statusDetail = 'loading';
    this.sortField = sort.active;
    this.sortDirection = sort.direction;
    this.loadData();
  }

  private loadData() {
    // Load companies
    this.subs.sink = this.freeCompaniesService.listFreeCompanies(this.pageIndex, this.pageSize, this.sortField, this.sortDirection, this.search)
      .pipe(tap(() => (this.statusDetail = 'success')))
      .subscribe((rta: any) => {
        this.dataSource.data = rta.companies
        .map((company: FreeCompanies) => {
          if (company.allied_id) {
            let groupId;
            if(company.group_id){
              groupId = company.group_id;
            }
            this.onAlliedChange(company.allied_id, company, false);
            if(groupId){
              company.group_id = groupId;
            }
          }
          if (company.group_id) {
            this.onGroupChange(company.group_id, company, false);
          }
          if(!company.company_name) {
            company.company_name = 'NO APLICA'
          }
          return company;
        });

        this.dataSource.data = this.dataSource.data.map(element => ({
          ...element,
          original: {
            allied_id: element.allied_id,
            group_id: element.group_id,
            facilitator_id: element.facilitator_id,
            id: element.id
          }
        }));
        this.length = rta.totalElements;
        this.totalItemsList.emit(rta.totalItems);
      });
  }

  // Filter groups and facilitators based on selected allied
  getFilteredGroups(alliedId: number): any[] {
    return this.groupList.filter(group => group.id_allied === alliedId);
  }

    // Update changes tracking
  private markAsChanged() {
    this.hasChanges = this.haveChangeInCompanies();
    this.freeCompaniesService.setChanges(this.hasChanges);
  }

  private haveChangeInCompanies(): boolean {
    let hasChanges = false;
    
    this.dataSource.data.some(company => {
      if (this.hasRecordChanged(company)) {
        hasChanges = true;
        return true;
      }
      return false;
    });
  
    return hasChanges;
  }

  getFilteredFacilitators(groupId: number): any[] {
    return this.facilitatorList.filter(facilitator => facilitator.id_allied === groupId);
  }

  onAlliedChange(newValue: string, element: FreeCompanies, updateChanges: boolean = true) {
    if(newValue !== 'SELECCIONAR' && newValue !== ''){
      element.allied_id = newValue;
      element.group_id = null;
      element.facilitator_id = null;
      if(updateChanges) this.markAsChanged();
    }
  }

  onGroupChange(newValue: any, element: FreeCompanies, updateChanges: boolean = true) {
    if(newValue !== 'SELECCIONAR' && newValue !== ''){
      element.group_id = newValue;
      element.facilitator_id = null;
      if(updateChanges) this.markAsChanged();
    }
  }

  onFacilitatorChange(newValue: any, element: FreeCompanies) {
    if(newValue !== 'SELECCIONAR' && newValue !== ''){
      element.facilitator_id = newValue;
      this.markAsChanged();
    }
  }

  trackByFn(index: number, item: FreeCompanies): any {
    return item.id;
  }

  private getCompanyEntity(company: any) {
    return {
      allied_id: company.allied_id,
      group_id: company.group_id,
      facilitator_id: company.facilitator_id,
      id: company.id,
    };
  }

  updateAnyCompany(company: any) {
    const request_company = this.getCompanyEntity(company);
    let change = true;

    this.subs.sink = this.translateService.get(['COMPANY.company-created', 'USERS.undo']).subscribe(
      (valor: any) => {
        const message = valor['COMPANY.company-created'];
        const action = valor['USERS.undo'];
        this._snackBar.open(message, action, {
          duration: 1500,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });

        this._snackBar._openedSnackBarRef?.onAction().subscribe(() => {
          change = false;
        });

        this._snackBar._openedSnackBarRef?.afterDismissed().subscribe(() => {
          if (change === true) {
            this.freeCompaniesService.updateAnyCompany(request_company).subscribe(() => {
              this.loadData();
            });
          }
        });
      }
    );
  }

  disabledCompany(element: any) {
    const id = element.id;
    let change = true;
    
    this.subs.sink = this.translateService.get(['COMPANY.company-disabled', 'USERS.undo']).subscribe(
      (valor: any) => {
        const message = valor['COMPANY.company-disabled'];
        const action = valor['USERS.undo'];
        this._snackBar.open(message, action, {
          duration: 1500,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
  
        this._snackBar._openedSnackBarRef?.onAction().subscribe(() => {
          change = false;
        });
  
        this._snackBar._openedSnackBarRef?.afterDismissed().subscribe(() => {
          if (change === true) {
            this.freeCompaniesService.disabledCompany(id).subscribe(() => {
              window.location.reload();
            });
          }
        });
      }
    );
  }
  

  enableButtomUpdate(element: any): boolean {
    const hasValidId = element.id !== null && element.id !== '';
    if(!hasValidId) return true;
    const hasValidFields = element.allied_id !== null && element.allied_id !== '' ||
                           element.group_id !== null && element.group_id !== '' ||
                           element.facilitator_id !== null && element.facilitator_id !== '';
    const hasChanges = this.hasRecordChanged(element);
    if(hasValidFields && !hasChanges)
      return true;
    if (hasValidFields || hasChanges)
      return false;
    return true
  }

  hasRecordChanged(element: any): boolean {
    return element.allied_id !== element.original.allied_id ||
           element.group_id !== element.original.group_id ||
           element.facilitator_id !== element.original.facilitator_id;
  }

  updateAllCompanies() {
    const allEntities = this.dataSource.data.map(company => {
      return this.getCompanyEntity(company)
    })
    let change = true;
    this.subs.sink = this.translateService.get(['COMPANY.company-created', 'USERS.undo']).subscribe(

      (valor: any) => {
        const message = valor['COMPANY.company-created'];
        const action = valor['USERS.undo'];
        this._snackBar.open(message, action, {
          duration: 1500,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
  
        this._snackBar._openedSnackBarRef?.onAction().subscribe(() => {
          change = false;
        });
  
        this._snackBar._openedSnackBarRef?.afterDismissed().subscribe(() => {
          if (change === true) {
            this.freeCompaniesService.updateAllCompanies(allEntities).subscribe(() => {
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            });
          }
        });
      }
    );
  }

  public handlePageEvent(ev: PageEvent): void {
    this.statusDetail = 'loading';
    this.pageIndex = ev.pageIndex + 1;
    this.pageSize = ev.pageSize;
    this.loadData();
    this.statusDetail = 'success';
  }
}
